import React from 'react';
import { AccountTypeSection } from './AccountTypeSection';
import styled from 'styled-components';
import { Text, TextButton, useIsMobile, Icon } from '@amzn/storm-ui';
import { spinner } from '@amzn/storm-ui-icons';
import { useAccountContext } from './AccountContextProvider';
import { useBundle } from '@amzn/react-arb-tools';
import { AccountTypes } from './Types';
import { AccountSelectorMetricsPublisher, InterstitialPageMetricsPublisher } from '../metrics';

const AccountListContainer = styled.div`
  overflow-y: auto;
  flex: 1;
  && {
    margin: 0 -4px;
    padding: 0 4px 4px 4px;
  }
`;

const AccountSelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
`;

const AccountSelectorLabel = styled(Text)`
  padding-top: 12px;
  padding-inline-start: 14px;
`;

const ChangeAccountTextButton = styled(TextButton)`
  padding-top: 12px;
  padding-inline-start: 14px;
`

const NoAccountsFoundText = styled(Text)`
  padding-top: 12px;
  text-align: center;
`;

export const AccountSelector: React.VFC = () => {
  const [bundle, isBundleLoading] = useBundle('pages.interstitial');
  const {
    hasNestedAccounts,
    filteredAccounts,
    setSelectedAccount,
    preSelectedAccount,
    setPreSelectedAccount,
    selectedAccountType
  } = useAccountContext();
  const isMobile = useIsMobile();

  if (isBundleLoading) {
    return <Icon type={spinner}></Icon>
  }

  const accountLabel = bundle.getMessage('account');
  const noAccountsFoundLabel = bundle.getMessage('no_accounts_found');
  const changeAccountLabel = bundle.getMessage('change_account');

  const accounts = preSelectedAccount && !isMobile ?
    [{accountType: selectedAccountType, label: '', accounts: [preSelectedAccount]}] : filteredAccounts;
  if (preSelectedAccount) {
    setSelectedAccount(preSelectedAccount);
  }
  const numOfAccounts = accounts.reduce((count, account) => count + account.accounts.length, 0);
  if (numOfAccounts === 0) {
    InterstitialPageMetricsPublisher.publishCounterMonitor('no_account', 1);
  }

  const changeAccount = () => {
    AccountSelectorMetricsPublisher.publishCounterMonitor('change_account', 1);
    setPreSelectedAccount(undefined);
  }

  return (
    <AccountSelectorContainer>
      {hasNestedAccounts && !isMobile && (<AccountSelectorLabel type="h3" styleAs="h4">{accountLabel}</AccountSelectorLabel>)}
      <AccountListContainer
        onKeyDown={(e) => {
          if (e.key === 'Escape' && document.activeElement instanceof HTMLElement) document.activeElement.blur()
        }}>
          {accounts.map((account) => {
            if (!account || !account.accounts || account.accounts.length === 0) {
              return null;
            }

            return (
              <AccountTypeSection key={account.accountType} account={account} />
            )
          })}
          {preSelectedAccount && !isMobile &&
            <ChangeAccountTextButton onClick={changeAccount} data-takt-id="interstitial-change-account">{changeAccountLabel}</ChangeAccountTextButton>}
          {numOfAccounts === 0 && <NoAccountsFoundText textColor="secondary">{noAccountsFoundLabel}</NoAccountsFoundText>}
      </AccountListContainer>
    </AccountSelectorContainer>
  );
}
